import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useFormik } from 'formik'
import * as yup from 'yup'

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { selectRoles, selectUseEvents } from 'src/v3/settings/settings.slice'
import AddIcon from '@mui/icons-material/Add'
import {
  CalendarIcon,
  ClockIcon,
  CopyIcon,
  DeleteIcon,
  EditIcon,
  EventIcon,
  GroupIcon,
  LeftArrowIcon,
  LightbulbIcon,
  LocationIcon,
  RightArrowIcon,
  StudentIcon,
} from 'src/v3/core/icons'
import Loading from 'src/v3/components/loading'
import {
  fetchCapacity,
  fetchEventCompliances,
  fetchEvents,
  fetchInstructors,
  fetchInvites,
  fetchSessions,
  selectActiveEvent,
  selectEventById,
  selectEventCompliancesByID,
  selectEventCompliancesStatus,
  selectEventsStatus,
  selectInstructorsByLSID,
  selectInstructorsStatus,
  selectInvites,
  selectInvitesStatus,
  selectSessionCapacity,
  selectSessionCapacityStatus,
  selectInstructors,
  selectSessionsByEventID,
  selectSessionsStatus,
} from './events.slice'
import { useScrollToFormikError } from '../utilities/hooks/useScrollToFormikError'
import SelectInstructors, {
  defaultValue,
} from '../components/form-fields/select-instructor'
import {
  CapacityCategory as Category,
  EventWizardStep,
  SessionCapacity,
} from './types'
import InviteUsers from './invite-users'
import TimeZoneDropdown from '../components/form-fields/time-zone-dropdown'

dayjs.extend(utc)
dayjs.extend(timezone)

const CATEGORIES: Record<Category, string> = {
  total: 'Total',
  aemt: 'AEMT',
  emt: 'EMT',
  paramedic: 'Paramedic',
  registeredNurse: 'Registered Nurse',
  other: 'Other',
}

let defaultCapacity: SessionCapacity = {
  total: NaN,
  aemt: NaN,
  emt: NaN,
  paramedic: NaN,
  registeredNurse: NaN,
  other: NaN,
}

export default ({
  setStep,
  eventView,
}: {
  setStep: React.Dispatch<React.SetStateAction<EventWizardStep>>
  eventView: 'create' | 'edit'
}): ReactElement => {
  let dispatch = useAppDispatch()
  let { eventId } = useParams()

  let useEvents = useAppSelector(selectUseEvents)
  let roles = useAppSelector(selectRoles)
  let isAdmin = roles.includes('admin')

  let event = eventId
    ? useAppSelector(selectEventById(eventId))
    : useAppSelector(selectActiveEvent)
  let lsid = event?.linkedServiceID || ''
  let eventsStatus = useAppSelector(selectEventsStatus)
  let sessions = useAppSelector(selectSessionsByEventID(event?.id || ''))
  let sessionsStatus = useAppSelector(selectSessionsStatus)
  let instructors = useAppSelector(
    selectInstructorsByLSID(event?.linkedServiceID || 'FOAMfrat'),
  )
  let instructorsList = useAppSelector(selectInstructors)
  let instructorsById = useMemo(
    () => new Map(instructorsList.map((i) => [i.id, i])),
    [instructorsList],
  )
  let instructorNames = useMemo(
    () =>
      Object.fromEntries(
        sessions.map((s) => [
          s.id,
          (
            (s.instructorIDs.length ? s.instructorIDs : event?.instructorIDs) ??
            []
          )
            .map((id) => instructorsById.get(id)?.name)
            .filter(Boolean)
            .join(', ') || '',
        ]),
      ),
    [sessions, instructorsById, event?.instructorIDs],
  )
  let instructorsStatus = useAppSelector(selectInstructorsStatus)
  let eventCompliance = useAppSelector(
    selectEventCompliancesByID(event?.id || ''),
  )
  let eventCompliancesStatus = useAppSelector(selectEventCompliancesStatus)
  let sessionCapacity = useAppSelector(selectSessionCapacity)
  let sessionCapacityStatus = useAppSelector(selectSessionCapacityStatus)
  let invites = useAppSelector(selectInvites)
  let invitesStatus = useAppSelector(selectInvitesStatus)

  let [view, setView] = useState<'view' | 'create' | 'edit'>('view')
  let [checkedUsers, setCheckedUsers] = useState<string[]>([])
  let [instructorIDs, setInstructorIDs] = useState<string[]>([])
  let [capacity, setCapacity] = useState<SessionCapacity>(defaultCapacity)
  let [loading, setLoading] = useState<boolean>(false)
  let [copyMessage, setCopyMessage] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let form = useFormik({
    validationSchema: yup.object({
      id: yup.string(),
      name: yup.string(),
      instructors: yup
        .array()
        .test(() => instructorIDs.every((id) => id !== defaultValue)),
      sessionTime: yup.string().required(),
      endTime: yup
        .string()
        .test(
          (item, context) =>
            !item ||
            dayjs.utc(item).isAfter(dayjs.utc(context.parent.sessionTime)),
        ),
      registrationDeadline: yup
        .string()
        .test(
          (item, context) =>
            !item ||
            dayjs
              .utc(item)
              .isBefore(
                context.parent.endTime
                  ? dayjs.utc(context.parent.endTime)
                  : dayjs
                      .utc(context.parent.sessionTime)
                      .add(event?.hours || 1, 'h'),
              ),
        ),
      timeZone: yup.string().required(),
      capacity: yup
        .object()
        .test(
          () =>
            Object.keys(CATEGORIES).every(
              (value) =>
                typeof capacity[value as Category] === 'number' ||
                capacity[value as Category] === undefined,
            ) &&
            (Number.isNaN(capacity.total) ||
              (capacity.aemt || 0) +
                (capacity.emt || 0) +
                (capacity.paramedic || 0) +
                (capacity.registeredNurse || 0) +
                (capacity.other || 0) <=
                (capacity.total || 0)),
        ),
    }),
    initialValues: {
      id: '',
      name: '',
      instructors: [],
      sessionTime: '',
      endTime: '',
      registrationDeadline: '',
      timeZone: dayjs.tz.guess() || '',
      capacity: {},
    },
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => {
      setLoading(true)
      let body = {
        name: values.name || undefined,
        sessionTime: values.sessionTime,
        endTime: values.endTime || undefined,
        registrationDeadline: values.registrationDeadline || undefined,
        timeZone: values.timeZone,
        instructorIDs,
        userIDs: checkedUsers,
        capacity: Object.values(capacity).every((c) => Number.isNaN(c))
          ? undefined
          : capacity,
      }
      let request = !values.id
        ? axios.post('/api/event/session', { eventID: event?.id, ...body })
        : axios.put('/api/event/session', { id: values.id, ...body })
      request
        .then(pluckData)
        .then(() => {
          if (values.id) {
            dispatch(fetchCapacity(values.id))
            dispatch(fetchInvites(values.id))
          }
          return dispatch(fetchSessions())
        })
        .then(() => resetForm())
        .then(() => setView('view'))
        .catch((err) =>
          setErrorMessage(pluckError(err, `Session ${view} failed`)),
        )
        .finally(() => setLoading(false))
    },
  })

  let noneText =
    "You may overwrite this event's regular instructors by selecting instructors for this session."

  useScrollToFormikError(form)

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (sessionsStatus === 'uninitialized') {
      dispatch(fetchSessions())
    }
  }, [sessionsStatus])

  useEffect(() => {
    if (instructorsStatus === 'uninitialized') {
      dispatch(fetchInstructors())
    }
  }, [instructorsStatus])

  useEffect(() => {
    if (
      form.values.id &&
      (!invitesStatus[form.values.id] ||
        invitesStatus[form.values.id] === 'uninitialized')
    ) {
      dispatch(fetchInvites(form.values.id))
    } else if (invitesStatus[form.values.id] === 'fulfilled') {
      setCheckedUsers(invites[form.values.id]?.map((inv) => inv.userID) || [])
    }
  }, [invitesStatus, form.values.id])

  useEffect(() => {
    if (
      form.values.id &&
      (!sessionCapacityStatus[form.values.id] ||
        sessionCapacityStatus[form.values.id] === 'uninitialized')
    ) {
      dispatch(fetchCapacity(form.values.id))
    } else if (sessionCapacityStatus[form.values.id] === 'fulfilled') {
      setCapacity(sessionCapacity[form.values.id] || defaultCapacity)
    }
  }, [sessionCapacityStatus, form.values.id])

  useEffect(() => {
    if (eventCompliancesStatus === 'uninitialized') {
      dispatch(fetchEventCompliances())
    }
  }, [eventCompliancesStatus])

  let isCompliant =
    !!lsid ||
    !!event?.featured ||
    Object.values(eventCompliance || {}).every(
      (status) => status === 'approved' || status === eventId,
    )

  let previousButton = isAdmin ? (
    <Button
      sx={{ color: 'text.primary' }}
      startIcon={<LeftArrowIcon />}
      onClick={() => setStep('syllabus')}
    >
      Previous: Event Syllabus
    </Button>
  ) : (
    <Button
      sx={{ color: 'text.primary' }}
      startIcon={<LeftArrowIcon />}
      onClick={() => setStep('details')}
    >
      Previous: Event Details
    </Button>
  )

  return useEvents ? (
    <Box>
      {(view === 'create' || view === 'edit') && (
        <form onSubmit={form.handleSubmit} style={{ marginTop: 16 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {view === 'create' ? 'Add New' : 'Edit'} Session
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Schedule event sessions and configure session details
              </Typography>
            </Box>
            <Button
              sx={{
                color: 'text.primary',
                alignSelf: 'flex-start',
              }}
              startIcon={<LeftArrowIcon />}
              onClick={() => {
                form.resetForm()
                setCapacity({ ...defaultCapacity })
                setCheckedUsers([])
                setView('view')
              }}
            >
              Back to Sessions
            </Button>
          </Box>
          <TextField
            sx={{ bgcolor: 'background.paperDark', mb: 2 }}
            fullWidth
            variant="outlined"
            value={form.values.name}
            label="Name (Optional)"
            name="name"
            onChange={form.handleChange}
            error={form.touched.name && !!form.errors.name}
          />
          <TimeZoneDropdown
            sx={{ mb: 2 }}
            fullWidth
            label="Session Time Zone"
            variant="outlined"
            value={form.values.timeZone}
            onChange={(value: string) => form.setFieldValue('timeZone', value)}
            error={form.touched.timeZone && !!form.errors.timeZone}
          />
          {form.values.timeZone && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <DateTimePicker
                  key={`session-time-${form.values.timeZone}`}
                  sx={{ backgroundColor: 'background.paperDark', flexGrow: 1 }}
                  label="Session Time"
                  ampm={false}
                  onChange={(val) => {
                    if (val) {
                      form.setFieldValue('sessionTime', dayjs.utc(val).format())
                    }
                  }}
                  value={
                    !form.values.sessionTime
                      ? null
                      : dayjs
                          .utc(form.values.sessionTime)
                          .tz(form.values.timeZone)
                  }
                  slotProps={{
                    textField: {
                      error:
                        form.touched.sessionTime && !!form.errors.sessionTime,
                    },
                  }}
                />
                <DateTimePicker
                  key={`end-time-${form.values.timeZone}`}
                  sx={{ backgroundColor: 'background.paperDark', flexGrow: 1 }}
                  label="Session End Time (Optional)"
                  ampm={false}
                  onChange={(val) => {
                    if (val) {
                      form.setFieldValue('endTime', dayjs.utc(val).format())
                    }
                  }}
                  value={
                    !form.values.endTime
                      ? null
                      : dayjs.utc(form.values.endTime).tz(form.values.timeZone)
                  }
                  slotProps={{
                    textField: {
                      error: form.touched.endTime && !!form.errors.endTime,
                    },
                  }}
                />
                <DateTimePicker
                  key={`registration-deadline-${form.values.timeZone}`}
                  sx={{ backgroundColor: 'background.paperDark', flexGrow: 1 }}
                  label="Registration Deadline (Optional)"
                  ampm={false}
                  onChange={(val) => {
                    if (val) {
                      form.setFieldValue(
                        'registrationDeadline',
                        dayjs.utc(val).format(),
                      )
                    }
                  }}
                  value={
                    !form.values.registrationDeadline
                      ? null
                      : dayjs
                          .utc(form.values.registrationDeadline)
                          .tz(form.values.timeZone)
                  }
                  slotProps={{
                    textField: {
                      error:
                        form.touched.registrationDeadline &&
                        !!form.errors.registrationDeadline,
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
          )}
          <SelectInstructors
            instructorIDs={instructorIDs}
            setInstructorIDs={setInstructorIDs}
            instructors={instructors}
            apiPath="event"
            fetchInstructors={fetchInstructors}
            linkedServiceID={lsid}
            setErrorMessage={setErrorMessage}
            submitted={!!form.submitCount}
            noneText={noneText}
          />
          <Box>
            <Divider
              sx={{ my: 3, borderColor: 'primary.dark', opacity: 0.5 }}
            />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Set Session Capacity
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              You may optionally set a limit on how many users can attend this
              session. Session capacity can be set as a total and/or as
              subtotals of users' primary license types.
            </Typography>
            {Object.entries(CATEGORIES).map(([category, name]) => {
              let limit = capacity[category as Category]
              let isUncapped = Number.isNaN(limit) || limit === undefined
              let displayError =
                form.submitCount > 0 &&
                form.errors.capacity &&
                category === 'total'

              return (
                <Box
                  key={category}
                  sx={{ display: 'flex', alignItems: 'flex-end' }}
                >
                  <FormControl>
                    <FormLabel>{name}</FormLabel>
                    <RadioGroup
                      row
                      value={isUncapped ? 'uncapped' : 'capped'}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCapacity({
                          ...capacity,
                          [category]: e.target.value === 'uncapped' ? NaN : 0,
                        })
                      }
                    >
                      <FormControlLabel
                        value="uncapped"
                        control={<Radio />}
                        label="Uncapped"
                      />
                      <FormControlLabel
                        value="capped"
                        control={<Radio />}
                        label="Capped"
                      />
                    </RadioGroup>
                  </FormControl>
                  <TextField
                    size="small"
                    label={`${name} User Cap`}
                    value={isUncapped ? 'None' : limit}
                    disabled={isUncapped}
                    onChange={(e) =>
                      setCapacity({
                        ...capacity,
                        [category]: Number.isNaN(e.target.value)
                          ? limit
                          : parseInt(e.target.value || '0', 10),
                      })
                    }
                    error={displayError}
                    helperText={
                      displayError &&
                      'The total user cap must equal or exceed the sum of the other license caps.'
                    }
                  />
                </Box>
              )
            })}
            <Divider
              sx={{ my: 3, borderColor: 'primary.dark', opacity: 0.5 }}
            />
          </Box>
          {event?.linkedServiceID && (
            <Box sx={{ mt: 3 }}>
              <InviteUsers
                sessionID={form.values.id}
                eventID={event?.id}
                checkedUsers={checkedUsers}
                setCheckedUsers={setCheckedUsers}
              />
            </Box>
          )}
          {isAdmin && (
            <Box sx={{ mt: 3 }}>
              <InviteUsers
                sessionID={form.values.id}
                eventID={event?.id}
                checkedUsers={checkedUsers}
                setCheckedUsers={setCheckedUsers}
                adminOnly
              />
            </Box>
          )}
          <Box
            sx={{
              mt: 5,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            {previousButton}
            <Box sx={{ flexGrow: '1' }}></Box>
            <Button variant="contained" type="submit">
              Save Session
            </Button>
          </Box>
        </form>
      )}
      {view === 'view' &&
        (!sessions.length ? (
          <>
            <Box
              sx={{ my: 5, display: 'flex', justifyContent: 'space-between' }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {event?.name}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  Manage your event sessions
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box
                sx={{
                  p: 4,
                  bgcolor: 'background.paperDark',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <Avatar
                  sx={{ height: 72, width: 72, bgcolor: 'background.paper' }}
                >
                  <EventIcon fontSize="large" color="primary" />
                </Avatar>
                <Typography variant="h6">No Sessions Added Yet</Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{
                    textAlign: 'center',
                    maxWidth: 450,
                    lineHeight: '16px',
                  }}
                >
                  Create your first session by clicking the button below. You
                  can add multiple sessions for different days or topics.
                </Typography>
                <Button
                  sx={{ py: 1.25, mt: 1.5 }}
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => setView('create')}
                  disabled={!isCompliant}
                >
                  Add First Session
                </Button>
              </Box>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paperDark',
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                  <LightbulbIcon color="primary" />
                  <Typography>Tips for Creating Sessions</Typography>
                </Box>{' '}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        color: 'primary.main',
                        display: 'flex',
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <ClockIcon />
                      <Typography>Flexible Scheduling</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Set up multiple sessions across different days and times
                      to accommodate various schedules
                    </Typography>
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        color: 'primary.main',
                        display: 'flex',
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <LocationIcon />
                      <Typography>Location Options</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Choose between in-person or virtual sessions with
                      automatic link generation.
                    </Typography>
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        color: 'primary.main',
                        display: 'flex',
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <GroupIcon />
                      <Typography>Attendee Management</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Manage RSVPs for each individual session.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                mt: 5,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              {previousButton}
              <Button
                sx={{ color: 'text.primary' }}
                endIcon={<RightArrowIcon />}
                onClick={() => setStep('quiz')}
              >
                Next: Quiz
              </Button>
            </Box>
          </>
        ) : (
          <>
            {eventView === 'create' ? (
              <Box
                sx={{
                  mt: 5,
                  mb: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {event?.name}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    Manage your event sessions
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setView('create')}
                    disabled={!isCompliant}
                  >
                    Add Session
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: -3,
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setView('create')}
                  disabled={!isCompliant}
                >
                  Add Session
                </Button>
              </Box>
            )}
            <Box sx={{ maxHeight: 600, overflow: 'scroll' }}>
              {sessions.map((session, i) => (
                <Card
                  key={session.id}
                  variant="outlined"
                  sx={{
                    bgcolor: 'background.paperDark',
                    py: 2.5,
                    pr: 2,
                    pl: 4,
                    mb: sessions.length === i + 1 ? 0 : 3,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography sx={{ fontSize: 18, mb: 1 }}>
                      {session.name}
                    </Typography>
                    <Box>
                      <IconButton
                        onClick={() => {
                          form.setFieldValue('id', session.id)
                          form.setFieldValue('name', session.name)
                          form.setFieldValue('sessionTime', session.sessionTime)
                          form.setFieldValue('endTime', session.endTime)
                          form.setFieldValue(
                            'registrationDeadline',
                            session.registrationDeadline,
                          )
                          form.setFieldValue('timeZone', session.timeZone)
                          setInstructorIDs(
                            session.instructorIDs.length
                              ? session.instructorIDs
                              : event?.instructorIDs || [],
                          )
                          setCapacity(
                            sessionCapacity[session.id] || defaultCapacity,
                          )
                          setCheckedUsers(
                            invites[session.id]?.map((inv) => inv.userID) || [],
                          )
                          setView('edit')
                        }}
                      >
                        <EditIcon sx={{ height: 18, width: 18 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setLoading(true)
                          axios
                            .delete(
                              `/api/event/session?sessionID=${session.id}`,
                            )
                            .then(() => dispatch(fetchSessions()))
                            .catch((err) =>
                              setErrorMessage(
                                pluckError(err, 'Error Deleting Session'),
                              ),
                            )
                            .finally(() => setLoading(false))
                        }}
                      >
                        <DeleteIcon sx={{ height: 18, width: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      fontSize: 14,
                      alignItems: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <Box>
                      <CalendarIcon
                        sx={{ height: 18, width: 18, mb: '2px', mr: '4px' }}
                      />
                      <Box component="span" color="text.secondary">
                        {dayjs
                          .utc(session.sessionTime)
                          .tz(form.values.timeZone)
                          .format('MMMM DD, YYYY')}
                      </Box>
                    </Box>
                    <Box>
                      <ClockIcon
                        sx={{ height: 18, width: 18, mb: '2px', mr: '2px' }}
                      />
                      <Box component="span" color="text.secondary">
                        {`${dayjs
                          .utc(session.sessionTime)
                          .tz(form.values.timeZone)
                          .format('h:mm A')} - ${(session.endTime
                          ? dayjs.utc(session.endTime).tz(form.values.timeZone)
                          : dayjs
                              .utc(session.sessionTime)
                              .tz(form.values.timeZone)
                              .add(event?.hours || 1, 'hours')
                        ).format('h:mm A (z)')}`}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      columnGap: 1.5,
                      fontSize: 14,
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <LocationIcon
                        color="primary"
                        sx={{ height: 18, width: 18, mb: '2px', mr: '2px' }}
                      />
                      <Box component="span">{event?.eventType}</Box>
                    </Box>
                    <Box>
                      <StudentIcon
                        color="primary"
                        sx={{ height: 18, width: 18, mb: '2px', mr: '6px' }}
                      />
                      <Box component="span">{instructorNames[session.id]}</Box>
                    </Box>
                    <Box>
                      {event?.location.kind !== 'virtual' && (
                        <>
                          <Typography color="textSecondary">
                            {event?.location?.name}
                          </Typography>
                        </>
                      )}
                      {event?.location.kind !== 'inPerson' && (
                        <>
                          <Button
                            color="primary"
                            startIcon={
                              <CopyIcon
                                sx={{
                                  height: 18,
                                  width: 18,
                                  ml: '2px',
                                  mr: '-2px',
                                }}
                              />
                            }
                            sx={{ ml: '-4px' }}
                            onClick={() => {
                              navigator.clipboard.writeText(
                                event?.location.kind !== 'inPerson'
                                  ? event?.location.url || ''
                                  : '',
                              )
                              setCopyMessage(true)
                            }}
                          >
                            Copy Link
                          </Button>
                          <Snackbar
                            color="secondary.main"
                            message="Copied to Clipboard"
                            autoHideDuration={2000}
                            onClose={() => setCopyMessage(false)}
                            open={copyMessage}
                          >
                            <Alert color="info">Copied to Clipboard</Alert>
                          </Snackbar>
                        </>
                      )}
                    </Box>
                    <Box>
                      <GroupIcon
                        color="primary"
                        sx={{ height: 18, width: 18, mb: '2px', mr: '6px' }}
                      />
                      <Box
                        component="span"
                        sx={{ fontSize: 14, color: 'text.secondary' }}
                      >
                        {session.invites.length || 0} Attendee
                        {session.invites.length === 1 ? '' : 's'}
                        {session.capacity &&
                          ` \u2022 ${session.capacity} Users Maximum`}
                      </Box>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
            <Box
              sx={{
                mt: 5,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              {previousButton}
              <Button
                sx={{ color: 'text.primary' }}
                endIcon={<RightArrowIcon />}
                onClick={() => setStep('quiz')}
              >
                Next: Quiz
              </Button>
            </Box>
          </>
        ))}
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading entities={[eventsStatus, sessionsStatus]} loading={loading} />
    </Box>
  ) : (
    <></>
  )
}
